import { useState } from 'react';
import { useGmbStrategyOverview, useGmbGoals, useGmbTechnicalPlan, useGenerateStrategy } from '../hooks/useGmbStrategy';
import type { StrategyOverviewData, GmbGoal, ActionPlanTask, RegenerationNotDueError } from '../types';
import TutorialCard from './TutorialCard';
import { toast } from 'sonner';

interface StrategyOverviewProps {
  onNavigate: (section: string) => void;
}

export default function StrategyOverview({ onNavigate }: StrategyOverviewProps) {
  const { data: overviewData, isLoading: overviewLoading, error: overviewError, refetch: refetchOverview } = useGmbStrategyOverview();
  const { data: goalsData, error: goalsError } = useGmbGoals();
  const { data: techPlanData, error: techError } = useGmbTechnicalPlan();
  const generateStrategy = useGenerateStrategy();
  const [regenError, setRegenError] = useState<RegenerationNotDueError | null>(null);
  const [isRegenDisabled, setIsRegenDisabled] = useState(false);

  const handleRegenerate = async () => {
    setRegenError(null);
    try {
      await generateStrategy.mutateAsync();
      toast.success('Strategy regenerated successfully');
      refetchOverview();
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 403 && data?.detail?.code === 'REGENERATION_NOT_DUE') {
        setRegenError(data.detail as RegenerationNotDueError);
        setIsRegenDisabled(true);
      } else {
        const msg = typeof data?.detail === 'object'
          ? data.detail.message
          : typeof data?.detail === 'string'
            ? data.detail
            : 'Failed to regenerate strategy';
        toast.error(msg);
      }
    }
  };

  if (overviewError || goalsError || techError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800">Failed to load strategy data</h3>
        <p className="text-xs text-red-600 mt-1">{(overviewError || goalsError || techError)?.message}</p>
        <button onClick={() => refetchOverview()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const overview: StrategyOverviewData = overviewData?.data || {} as StrategyOverviewData;
  const goals = goalsData?.data || {};
  const techPlan = techPlanData?.data || {};

  const stats = [
    {
      label: 'Goals Identified',
      value: goals.total_goals ? `${goals.goals_completed || 0}/${goals.total_goals}` : '—',
      change: goals.completion_rate ? `${goals.completion_rate}% on track` : 'Awaiting data',
      color: 'emerald',
      icon: 'ri-flag-line',
    },
    {
      label: 'Action Items',
      value: techPlan.total_tasks?.toString() || '—',
      change: techPlan.pending ? `⚠ ${techPlan.pending} due` : 'No tasks yet',
      color: 'violet',
      icon: 'ri-task-line',
    },
    {
      label: 'Local Keywords',
      value: overview.keywords_identified?.toString() || '—',
      change: 'From Keyword Planner',
      color: 'orange',
      icon: 'ri-hashtag',
    },
  ];

  const getBarColorForSnap = (color: string) => {
    switch (color) {
      case 'green': return 'bg-emerald-500';
      case 'orange': return 'bg-amber-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'emerald': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
      case 'violet': return 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800';
      case 'orange': return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      default: return 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };

  const GOAL_LABEL_MAP: Record<string, string> = {
    keyword_rank: 'Keyword Rank',
    website_clicks: 'Clicks',
    calls: 'Calls',
    directions: 'Directions',
    whatsapp: 'WhatsApp',
    messages: 'Messages',
    review_count: 'Reviews',
    avg_rating: 'Avg Rating',
  };

  const goalSummary = (overview.goal_setting_summary || []).slice(0, 4);
  const goalPills = goalSummary.map((g: any) => ({
    label: GOAL_LABEL_MAP[g.kpi] || g.kpi?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Goal',
    status: g.status === 'on_track' ? 'done' : g.status === 'at_risk' ? 'at-risk' : 'not-started',
  }));
  const goalsNeedAttention = goalPills.filter((p: any) => p.status !== 'done').length;

  const subsectionSnapshots = [
    {
      id: 'as-is-state',
      title: 'As-Is State',
      borderColor: 'blue',
      type: 'bars' as const,
      data: (overview.asis_summary || []).slice(0, 4).map((s: any) => ({
        label: s.category,
        value: s.score_pct,
        color: s.color,
      })),
      footer: overview.weakest_area ? `Weakest area: ${overview.weakest_area}` : 'Complete your profile audit',
    },
    {
      id: 'goal-setting',
      title: 'Goal Setting',
      borderColor: 'green',
      type: 'pills' as const,
      data: goalPills.length > 0
        ? goalPills
        : [
            { label: 'Keyword Rank', status: 'not-started' },
            { label: 'Clicks', status: 'not-started' },
            { label: 'Calls', status: 'not-started' },
            { label: 'Reviews', status: 'not-started' },
          ],
      footer: goalPills.length > 0
        ? goalsNeedAttention > 0
          ? `${goalsNeedAttention} goal${goalsNeedAttention > 1 ? 's' : ''} need${goalsNeedAttention === 1 ? 's' : ''} attention`
          : 'All goals on track'
        : 'No goals set yet',
    },
    {
      id: 'action-plan',
      title: 'Action Plan',
      borderColor: 'purple',
      type: 'progress' as const,
      data: [
        { label: 'One-Time Setup', current: techPlan.setup_completed || 0, total: techPlan.total_setup_tasks || 10 },
        { label: 'Weekly Maintenance', current: techPlan.maintenance_completed || 0, total: techPlan.total_maintenance_tasks || 5 },
      ],
      footer: `${techPlan.pending || 0} items overdue`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Row 1 — Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {overviewLoading
          ? [1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse"></div>
                </div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></div>
              </div>
            ))
          : stats.map((stat) => (
              <div key={stat.label} className={`rounded-xl border p-5 transition-all relative group ${getColorClasses(stat.color)}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{stat.label}</span>
                  <div className="w-8 h-8 rounded-lg bg-white/60 dark:bg-white/10 flex items-center justify-center">
                    <i className={`${stat.icon} text-base`}></i>
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-xs font-medium opacity-80">{stat.change}</div>
              </div>
            ))
        }
      </div>

      {/* Regenerate Strategy */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Strategy Generation</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Strategy is auto-generated on first sync and re-checked on each sync. You can also regenerate manually (available every 4 weeks).
            </p>
          </div>
          <button
            onClick={handleRegenerate}
            disabled={generateStrategy.isPending || isRegenDisabled}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {generateStrategy.isPending ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="ri-refresh-line"></i>
                Regenerate Strategy
              </>
            )}
          </button>
        </div>
        {regenError && (
          <div className="mt-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="ri-time-line text-amber-600 dark:text-amber-400 mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Strategy regeneration not due yet</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Next eligible date: {regenError.eligible_date} ({regenError.days_remaining} days remaining)
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Row 2 — Tutorial Banner */}
      <TutorialCard
        title="How GMB Manager Works"
        description="Learn the basics of managing your Google Business Profile"
        duration="3:45 min"
        variant="emerald"
      />

      {/* Row 4 — Subsection Snapshots */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {overviewLoading
          ? [1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 border-l-4 border-l-gray-200 dark:border-l-gray-700">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-4 animate-pulse"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full animate-pulse"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6 animate-pulse"></div>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32 mt-4 animate-pulse"></div>
              </div>
            ))
          : subsectionSnapshots.map((snap) => (
          <button
            key={snap.id}
            onClick={() => onNavigate(snap.id)}
            className={`bg-white dark:bg-gray-800 rounded-xl border p-5 text-left hover:shadow-md transition-all cursor-pointer border-l-4 ${
              snap.borderColor === 'blue'
                ? 'border-l-sky-500'
                : snap.borderColor === 'green'
                  ? 'border-l-emerald-500'
                  : 'border-l-violet-500'
            } border-gray-200 dark:border-gray-700`}
          >
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{snap.title}</h4>

            {snap.type === 'bars' && (
              <div className="space-y-2">
                {snap.data.map((bar) => (
                  <div key={bar.label} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 truncate" title={bar.label}>{bar.label}</span>
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${getBarColorForSnap(bar.color)}`} style={{ width: `${bar.value}%` }}></div>
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-8 text-right">{bar.value}%</span>
                  </div>
                ))}
              </div>
            )}

            {snap.type === 'pills' && (
              <div className="grid grid-cols-2 gap-2">
                {snap.data.map((pill) => (
                  <span
                    key={pill.label}
                    className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium ${
                      pill.status === 'done'
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                        : pill.status === 'at-risk'
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <i className={`mr-1 ${
                      pill.status === 'done'
                        ? 'ri-checkbox-circle-line'
                        : pill.status === 'at-risk'
                          ? 'ri-error-warning-line'
                          : 'ri-subtract-line'
                    }`}></i>
                    {pill.label}
                  </span>
                ))}
              </div>
            )}

            {snap.type === 'progress' && (
              <div className="space-y-3">
                {snap.data.map((prog) => (
                  <div key={prog.label}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{prog.label}</span>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{prog.current}/{prog.total}</span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-violet-500"
                        style={{ width: `${prog.total > 0 ? (prog.current / prog.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">{snap.footer}</p>
          </button>
        ))
        }
      </div>
    </div>
  );
}
